import { generateErrorMessageFromStatusCode, WorkflowStatusCode } from '@gitlab-lsp/workflow-api';
import type { AIContextItem } from '@gitlab-org/ai-context';
import type { UserAction } from '../../backend/backend';
import { type PresentationPolicy, promptGoal } from '../../backend/presentation_policy';
import type { ParsedCommand } from '../../parse';

// Headless resume (`duo run` with a resume id): no `/new` to suggest, so omit it.
const RESUMED_SESSION_ENDED_HEADLESS_MESSAGE = 'Previous session has ended and cannot be resumed.';

type RunCommand = Extract<ParsedCommand, { name: 'run' }>;

/**
 * Headless `duo run` (local) and rails CI. No interactive user: the system
 * context is (re)sent every turn, and there is no `/new` recovery. (How an
 * acquire failure is surfaced — fail-fast vs display — is the controller's job,
 * not the policy's.)
 *
 * Built via {@link fromRunCommand} from the already-narrowed `run` command, so
 * it holds the goal and context items directly rather than re-narrowing an
 * injected ParsedCliInput on every read.
 */
export class HeadlessPresentationPolicy implements PresentationPolicy {
  #goal: string;

  #aiContextItems: AIContextItem[];

  // The composition root has already narrowed the command to the `run` variant;
  // this keeps that narrowing (and the knowledge of which fields the policy
  // needs) next to the policy instead of in di.ts.
  static fromRunCommand(command: RunCommand): HeadlessPresentationPolicy {
    return new HeadlessPresentationPolicy(command.goal, command.aiContextItems ?? []);
  }

  constructor(goal: string, aiContextItems: AIContextItem[] = []) {
    this.#goal = goal;
    this.#aiContextItems = aiContextItems;
  }

  readonly includeSystemContextEveryTurn = true;

  readonly supportsScheduledWakes = false;

  initialUserContextItems(): AIContextItem[] {
    return this.#aiContextItems;
  }

  sessionEndedMessage(): string {
    // "failed to start" is inaccurate but headless has no session-ended UX; the
    // status-code message is the closest terminal notice a single-turn run has.
    return generateErrorMessageFromStatusCode(WorkflowStatusCode.FAILED_TO_START);
  }

  resumedSessionEndedMessage(): string {
    return RESUMED_SESSION_ENDED_HEADLESS_MESSAGE;
  }

  resolveGoal(action: UserAction): string {
    return promptGoal(action) ?? this.#goal;
  }
}
